import type { GetServerSidePropsContext } from "next";
import Link from "next/link";
import { NextSeo } from "next-seo";
import { UserLoginForm } from "@/components/auth/user-login-form";
import { EtherealShadow } from "@/components/landing/ethereal-shadow";
import { getLayout } from "@/components/layouts/centered";
import { LogoIcon } from "@/components/logo-icon";
import { EmployerPilotLoginCallout } from "@/components/pilot/employer-pilot-login-callout";
import { usePilotSelfServeLocked } from "@/components/providers/pilot-flag-provider";
import { getPreferredSignInProviderFromRequest } from "@/lib/auth/preferred-sign-in-provider";
import { isQaLoginEnabled } from "@/lib/auth/qa-login";
import { getPilotSelfServeLockedPageProps } from "@/lib/pilot-flag-page-props";

type LoginPageProps = {
  preferredProvider: ReturnType<typeof getPreferredSignInProviderFromRequest>;
  qaLoginEnabled: boolean;
};

export default function LoginPage({
  preferredProvider,
  qaLoginEnabled,
}: LoginPageProps) {
  const showEmployerPilotCallout = usePilotSelfServeLocked();

  return (
    <>
      <NextSeo
        canonical="https://www.vacwork.app/login"
        description="Login to your VacWork account to access job listings, manage applications, and more. Secure and easy sign-in."
        title="Login | VacWork"
      />

      {/* Header - pilot banner + logo */}
      <div className="fixed top-0 right-0 left-0 z-50 w-full">
        {showEmployerPilotCallout ? <EmployerPilotLoginCallout /> : null}
        <header className="w-full">
          <div className="p-6 md:p-8">
            <Link href="/">
              <LogoIcon />
            </Link>
          </div>
        </header>
      </div>

      {/* Main Layout */}
      <main className="flex h-full" id="main-content">
        {/* Login Form Section */}
        <div className="relative w-full lg:w-1/2">
          <div className="flex min-h-screen items-center justify-center overflow-hidden p-6 md:p-0">
            <div className="relative z-20 m-auto flex w-full max-w-[380px] flex-col py-8">
              <div className="relative flex w-full flex-col">
                <div className="flex flex-col space-y-2 pb-4 text-left">
                  <h1 className="font-semibold text-2xl tracking-tight">
                    Login to VacWork
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    Sign in or create an account
                  </p>
                </div>
                <UserLoginForm
                  preferredProvider={preferredProvider}
                  qaLoginEnabled={qaLoginEnabled}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Background Section with EtherealShadow - Hidden on mobile, visible on desktop */}
        <div className="relative hidden overflow-hidden lg:flex lg:w-1/2">
          <EtherealShadow
            // color="hsl(var(--primary) / 0.6)"
            animation={{ scale: 1, speed: 200 }}
            className="z-0"
            noise={{ opacity: 0.1, scale: 1.0 }}
            sizing="fill"
          />

          {/* Gradient overlay */}
          <div className="absolute inset-0 z-10 bg-linear-to-br from-background/20 via-background/40 to-background/60" />

          {/* Empty content overlay for potential future use */}
          <div className="absolute inset-0 z-20" />
        </div>
      </main>
    </>
  );
}

LoginPage.getLayout = getLayout;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { req } = context;

  return {
    props: {
      preferredProvider: getPreferredSignInProviderFromRequest(req),
      qaLoginEnabled: isQaLoginEnabled(),
      ...(await getPilotSelfServeLockedPageProps(context)),
    },
  };
}
